import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import PageTitle from "../../components/PageTitle";
import PositiveButton from "../../components/PositiveButton";
import Ghost from "../../components/Ghost";
import useFeaturePromotionsService from "../../services/FeaturePromotionsService";
import {
  FeaturePromotion,
  featurePromotionStatus,
  parseUtc,
} from "../../types/FeaturePromotion";
import { findKnownFeatureKey } from "../../constants/featurePromotions";
import FeaturePromotionStatusBadge from "../../components/FeaturePromotions/FeaturePromotionStatusBadge";

export default function AdminFeaturePromotionsListPage() {
  const navigate = useNavigate();
  const { getFeaturePromotions } = useFeaturePromotionsService();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [promotions, setPromotions] = useState<FeaturePromotion[]>([]);

  // Keyed on includeEnded alone. The service functions change identity on every render (the auth
  // hook underneath isn't memoised), so depending on them would refetch in a loop.
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getFeaturePromotions(includeEnded)
      .then(setPromotions)
      .catch((e: Error) => {
        console.error("Error fetching feature promotions:", e);
        setError(e.message);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeEnded]);

  const formatTime = (value: string) => parseUtc(value).toLocaleString("en-GB");

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-row items-center gap-4">
        <PageTitle>Feature promotions</PageTitle>
        <PositiveButton onClick={() => navigate("/admin/featurepromotions/new")}>
          <AddIcon /> Add promotion
        </PositiveButton>
        <label className="flex items-center gap-2 ml-auto">
          <input
            type="checkbox"
            checked={includeEnded}
            onChange={(e) => setIncludeEnded(e.target.checked)}
          />
          Show ended
        </label>
      </section>

      {error && (
        <div className="text-white bg-red-800 py-2 px-4 rounded-xl">{error}</div>
      )}

      <section>
        <table className="table-auto w-full card border-spacing-2">
          <thead>
            <tr>
              <th className="font-normal text-left p-4">Feature</th>
              <th className="font-normal text-left p-4">Status</th>
              <th className="font-normal text-left p-4">Starts</th>
              <th className="font-normal text-left p-4">Ends</th>
              <th className="font-normal text-left p-4">Note</th>
              <th className="font-normal text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 3 }, (_, i) => (
                <tr className="border-t border-cyan-900" key={i}>
                  {Array.from({ length: 5 }, (_, j) => (
                    <td className="text-left p-4" key={j}>
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

            {!isLoading && promotions.length === 0 && (
              <tr className="border-t border-cyan-900">
                <td className="text-left p-4" colSpan={6}>
                  {includeEnded
                    ? "No feature promotions yet."
                    : "No current or upcoming feature promotions."}
                </td>
              </tr>
            )}

            {!isLoading &&
              promotions.map((promotion) => {
                const known = findKnownFeatureKey(promotion.featureKey);
                return (
                  <tr
                    className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                    key={promotion.id}
                    onClick={() => navigate(`/admin/featurepromotions/${promotion.id}`)}
                  >
                    <td className="text-left p-4">
                      <div>{known?.name ?? promotion.featureKey}</div>
                      <div className="text-xs opacity-75 font-mono">{promotion.featureKey}</div>
                    </td>
                    <td className="text-left p-4">
                      <FeaturePromotionStatusBadge status={featurePromotionStatus(promotion)} />
                    </td>
                    <td className="text-left p-4">{formatTime(promotion.startsAtUtc)}</td>
                    <td className="text-left p-4">{formatTime(promotion.endsAtUtc)}</td>
                    <td className="text-left p-4 text-sm opacity-75">{promotion.description}</td>
                    <td className="text-right p-4">
                      <ArrowForwardTwoToneIcon />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
